import React, { useContext, useEffect, useState } from 'react';
import { VariableSizeList } from 'react-window';
import { FormDataContext } from '../../contexts/FormDataContext';
import { SectionHeading } from '../SectionHeading';
import { IntakeAutocomplete } from '../IntakeAutocomplete';
import { autocompleteClasses, styled } from '@mui/material';

export const UniversityAutocomplete = () => {
  const [schoolOptions, setSchoolOptions] = useState([]);
  const {
    schools,
    setSelectedSchools,
    preferencesFormDisabled,
    selectedSchools,
  } = useContext(FormDataContext);

  useEffect(() => {
    if (schools.length > 0) {
      const uniqueSchools = [...new Map(
        schools.map(item => [item.name, item])).values()
      ];
      let options = [];
      uniqueSchools.forEach(school => {
        options.push({id: school.id, name: school.name});
      });
      setSchoolOptions(options);
    }
  }, [schools]);

  const LISTBOX_PADDING = 8; // px

  const OuterElementContext = React.createContext({});

  const OuterElementType = React.forwardRef((props, ref) => {
    const outerProps = React.useContext(OuterElementContext);
    return <div ref={ref} {...props} {...outerProps} />;
  });

  const StyledListbox = styled('ul')(({ theme }) => ({
    [`& .${autocompleteClasses.option}`]: {
      ...theme.typography.body1,
      display: 'flex',
      alignItems: 'center',
      padding: '6px 16px',
      boxSizing: 'border-box',
      whiteSpace: 'pre-wrap',
      wordWrap: 'break-word',
    },
  }));

  const ListboxComponent = React.forwardRef(function ListboxComponent(props, ref) {
    const { children, ...other } = props;
    const itemData = React.Children.toArray(children);

    const itemCount = itemData.length;

    // Measure the height of each item and cache the results
    const itemSizeCache = React.useRef({});
    const getItemSize = (index) => {
      if (itemSizeCache.current[index] != null) {
        return itemSizeCache.current[index];
      }

      const child = itemData[index];
      const estimatedHeight = 46;

      const div = document.createElement('div');
      div.style.visibility = 'hidden';
      div.style.position = 'absolute';
      div.style.height = 'auto';
      div.style.width = '100%';
      div.style.padding = '6px 16px';
      div.innerHTML = child.props.children;

      document.body.appendChild(div);
      const height = Math.ceil(div.clientHeight) + LISTBOX_PADDING * 2;
      document.body.removeChild(div);

      itemSizeCache.current[index] = height > estimatedHeight ? height : estimatedHeight;
      return itemSizeCache.current[index];
    };

    const height = Math.min(8 * 46, 240); // Default height for 8 items

    return (
      <div ref={ref}>
        <OuterElementContext.Provider value={other}>
          <VariableSizeList
            height={height}
            innerElementType={StyledListbox}
            itemData={itemData}
            itemSize={getItemSize}
            overscanCount={5}
            itemCount={itemCount}
            outerElementType={OuterElementType}
          >
            {({ data, index, style }) => {
              const child = data[index];
              return React.cloneElement(child, {
                style: {
                  ...style,
                  top: style.top + LISTBOX_PADDING,
                },
                className: autocompleteClasses.option,
              });
            }}
          </VariableSizeList>
        </OuterElementContext.Provider>
      </div>
    );
  });

  return (
    <>
      <SectionHeading>
        Your University
      </SectionHeading>
      {schoolOptions.length > 0 &&
        <IntakeAutocomplete
          ListboxComponent={ListboxComponent}
          label="universities"
          options={schoolOptions}
          selectedTaxonomyTermIds={selectedSchools}
          setSelectedTaxonomyTermIds={setSelectedSchools}
          id="checkboxesComboUniversities"
          disabled={preferencesFormDisabled}
        />
      }
    </>
  );
};